import React, {
  useState,
  useEffect,
  useRef,
  FormEvent,
  ChangeEvent,
} from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  InputGroup,
} from "react-bootstrap";
import { isInIframe } from "../../utils/is-in-iframe";
import { ToolsHeader } from "../components/ToolsHeader";
import { Box } from "./colorPickerStyle";

const ColorPicker: React.FC = () => {
  const [selectedColor, setSelectedColor] = useState<string>("#000ce7");
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    drawColorWheel();
  }, []);

  const drawColorWheel = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(centerX, centerY) - 10;

    for (let angle = 0; angle < 360; angle += 1) {
      for (let saturation = 100; saturation >= 0; saturation -= 2) {
        const radian = (angle - 90) * (Math.PI / 180);
        const x = centerX + (saturation / 100) * radius * Math.cos(radian);
        const y = centerY + (saturation / 100) * radius * Math.sin(radian);

        ctx.beginPath();
        ctx.arc(x, y, 2, 0, 2 * Math.PI);
        ctx.fillStyle = `hsl(${angle}, ${saturation}%, 50%)`;
        ctx.fill();
      }
    }
  };

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const imageData = ctx.getImageData(x, y, 1, 1).data;
    const color = `#${[...imageData]
      .slice(0, 3)
      .map((x) => x.toString(16).padStart(2, "0"))
      .join("")}`;
    setSelectedColor(color);
  };

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const input = e.currentTarget.elements.namedItem(
      "colorInput"
    ) as HTMLInputElement;
    setSelectedColor(input.value);
  };

  const handleColorInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSelectedColor(e.target.value);
  };

  const handleColorPickerChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSelectedColor(e.target.value);
  };

  const getRgbValues = (color: string): number[] => {
    const temp = document.createElement("div");
    temp.style.color = color;
    document.body.appendChild(temp);
    const computedColor = getComputedStyle(temp).color;
    document.body.removeChild(temp);
    return computedColor.match(/\d+/g)?.map(Number) || [0, 0, 0];
  };

  const getHslValues = (color: string): (number | string)[] => {
    const [r, g, b] = getRgbValues(color);
    const rgbToHsl = (r: number, g: number, b: number): (number | string)[] => {
      r /= 255;
      g /= 255;
      b /= 255;
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h: number,
        s: number,
        l: number = (max + min) / 2;

      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r:
            h = (g - b) / d + (g < b ? 6 : 0);
            break;
          case g:
            h = (b - r) / d + 2;
            break;
          case b:
            h = (r - g) / d + 4;
            break;
          default:
            h = 0;
        }
        h /= 6;
      }

      return [
        Math.round(h * 360),
        `${Math.round(s * 100)}%`,
        `${Math.round(l * 100)}%`,
      ];
    };
    return rgbToHsl(r, g, b);
  };

  const ensureHexColor = (color: string): string => {
    if (color.startsWith("#")) return color;
    const [r, g, b] = getRgbValues(color);
    return `#${r.toString(16).padStart(2, "0")}${g
      .toString(16)
      .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
  };

  const calculateShadeHex = (color: string, percent: number): string => {
    const [r, g, b] = getRgbValues(color);
    const factor = percent / 100;
    const calculateShade = (value: number) => Math.round(value * factor);
    return `#${calculateShade(r).toString(16).padStart(2, "0")}${calculateShade(
      g
    )
      .toString(16)
      .padStart(2, "0")}${calculateShade(b).toString(16).padStart(2, "0")}`;
  };

  const renderShades = () => {
    const shades = [];
    for (let i = 100; i >= 0; i -= 5) {
      shades.push(
        <div key={i} className="d-flex align-items-center mb-1">
          <span className="me-2" style={{ width: "3rem" }}>
            {i}%
          </span>
          <div
            className="flex-grow-1"
            style={{
              height: "1.5rem",
              backgroundColor: selectedColor,
              opacity: i / 100,
            }}
          />
          <span
            className="ms-2"
            style={{ width: "5rem", fontFamily: "monospace" }}
          >
            {calculateShadeHex(selectedColor, i)}
          </span>
        </div>
      );
    }
    return shades;
  };

  return (
    <Box>
      <Container
        className={`${!isInIframe ? "py-5 defaultText" : "py-1 interDisplay"
          }`}
      >
        {!isInIframe && (
          <ToolsHeader
            title="React Color Picker"
            description="Easily select and customize colors to perfectly match your
                design projects and needs."
          />
        )}
        <Row>
          <Col md={4}>
            <Card className="mb-4">
              <Card.Body style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Card.Title>Pick a Color</Card.Title>
                <canvas
                  ref={canvasRef}
                  width={300}
                  height={300}
                  className="rounded-circle mb-3"
                  style={{
                    cursor: "pointer",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                  onClick={handleCanvasClick}
                />
                <p style={{ fontFamily: "monospace" }}>
                  Selected Color: {selectedColor}
                </p>
              </Card.Body>
            </Card>
            <Card className="mb-4">
              <Card.Body>
                <Card.Title>Or Enter a Color</Card.Title>
                <Form onSubmit={handleFormSubmit}>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      name="colorInput"
                      placeholder="Color value"
                      value={selectedColor}
                      onChange={handleColorInputChange}
                    />
                    <Button type="submit" style={{ backgroundColor: "#8CC540" }}>
                      OK
                    </Button>
                  </InputGroup>
                </Form>
              </Card.Body>
            </Card>
            <Card>
              <Card.Body>
                <Card.Title>Or Use HTML5</Card.Title>
                <InputGroup>
                  <InputGroup.Text>👁</InputGroup.Text>
                  <Form.Control
                    type="color"
                    value={ensureHexColor(selectedColor)}
                    onChange={handleColorPickerChange}
                    style={{ height: "38px", padding: "0" }}
                  />
                </InputGroup>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="mb-4">
              <Card.Body>
                <Card.Title>Selected Color</Card.Title>
                <div
                  className="rounded mb-3 p-3 d-flex flex-column justify-content-between"
                  style={{
                    height: "12rem",
                    backgroundColor: selectedColor,
                  }}
                >
                  <span style={{ color: "black" }}>Black Text</span>
                  <span style={{ color: "black", opacity: 0.5 }}>Shadow</span>
                  <span style={{ color: "white" }}>White Text</span>
                  <span style={{ color: "white", opacity: 0.5 }}>Shadow</span>
                </div>
                <div style={{ fontFamily: "monospace" }}>
                  <p>{ensureHexColor(selectedColor)}</p>
                  <p>rgb({getRgbValues(selectedColor).join(", ")})</p>
                  <p>hsl({getHslValues(selectedColor).join(", ")})</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card>
              <Card.Body>
                <Card.Title>Lighter / Darker</Card.Title>
                {renderShades()}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </Box>
  );
};

export default ColorPicker;